/**
 * FacadeButton — LiveView hook attached to every light-DOM button element
 * with `phx-hook="FacadeButton"`.
 *
 * For hold-to-confirm buttons (`data-hold-state` present), this hook owns
 * the RAF/hold loop: it drives `--hold-progress` (0→1 over `data-hold-duration`
 * ms) and `data-hold-state` (idle → holding → confirmed) on the element, then
 * pushes the hold event to the server.
 *
 * Server-side, components can broadcast `reset_hold_button` to put the
 * button back into the idle state (e.g. when re-opening a confirmation
 * drawer after the user dismissed the action).
 */

type HookContext = {
  readonly el: HTMLElement
  _cleanup?: () => void
  _rafId?: number
  readonly pushEvent: (event: string, payload: Record<string, unknown>) => void
  readonly pushEventTo: (
    target: string,
    event: string,
    payload: Record<string, unknown>
  ) => void
  readonly handleEvent: (event: string, callback: () => void) => void
}

type DrawerElement = HTMLElement & { open: boolean }

const isHoldButton = (el: HTMLElement): boolean =>
  el.dataset.holdState !== undefined

const isDisabled = (el: HTMLElement): boolean =>
  el.hasAttribute('disabled') || el.dataset.holdState === 'confirmed'

const getHoldEvent = (el: HTMLElement): string =>
  el.dataset.holdEvent ?? 'hold_submit'

const getHoldDuration = (el: HTMLElement): number => {
  const raw = el.dataset.holdDuration

  return raw ? parseInt(raw, 10) : 1500
}

const hasBooleanOpen = (el: HTMLElement): el is DrawerElement =>
  'open' in el && typeof el.open === 'boolean'

const findDrawer = (el: HTMLElement): DrawerElement | null => {
  const id = el.dataset.drawerId

  if (!id) {
    return null
  }

  const drawer = document.getElementById(id)

  return drawer?.localName === 'wa-drawer' && hasBooleanOpen(drawer)
    ? drawer
    : null
}

const pushHoldEvent = (ctx: HookContext): void => {
  const event = getHoldEvent(ctx.el)
  const target = ctx.el.getAttribute('phx-target')

  if (target) {
    ctx.pushEventTo(target, event, {})
    return
  }

  ctx.pushEvent(event, {})
}

const resetHoldState = (el: HTMLElement): void => {
  el.dataset.holdState = 'idle'
  el.style.removeProperty('--hold-progress')
}

const cancelHold = (ctx: HookContext): void => {
  if (ctx._rafId !== undefined) {
    cancelAnimationFrame(ctx._rafId)
    ctx._rafId = undefined
  }
}

const startHoldLoop = (ctx: HookContext): void => {
  const el = ctx.el
  const duration = getHoldDuration(el)
  const start = performance.now()

  el.dataset.holdState = 'holding'

  const tick = (now: number): void => {
    const progress = Math.min((now - start) / duration, 1)

    el.style.setProperty('--hold-progress', String(progress))

    if (progress >= 1) {
      ctx._rafId = undefined
      el.dataset.holdState = 'confirmed'
      const drawer = findDrawer(el)

      if (drawer) {
        drawer.open = false
      }

      pushHoldEvent(ctx)
      return
    }

    ctx._rafId = requestAnimationFrame(tick)
  }

  ctx._rafId = requestAnimationFrame(tick)
}

const attachHoldListeners = (ctx: HookContext): void => {
  ctx._cleanup?.()

  const el = ctx.el

  const onPointerDown = (): void => {
    if (isDisabled(el)) {
      return
    }

    cancelHold(ctx)
    startHoldLoop(ctx)
  }

  const onPointerCancel = (): void => {
    cancelHold(ctx)
    resetHoldState(el)
  }

  el.addEventListener('pointerdown', onPointerDown)
  el.addEventListener('pointerup', onPointerCancel)
  el.addEventListener('pointerleave', onPointerCancel)

  ctx._cleanup = () => {
    el.removeEventListener('pointerdown', onPointerDown)
    el.removeEventListener('pointerup', onPointerCancel)
    el.removeEventListener('pointerleave', onPointerCancel)
    cancelHold(ctx)
  }
}

export const FacadeButton = {
  mounted(this: HookContext) {
    if (isHoldButton(this.el)) {
      attachHoldListeners(this)
    }

    // Registered once — LiveView does not support deregistration, so updates
    // must not re-register this binding (stacking causes duplicate events).
    this.handleEvent('reset_hold_button', () => {
      resetHoldState(this.el)
    })
  },

  updated(this: HookContext) {
    // Re-attach only the DOM listeners; the server binding registered in
    // mounted stays active for the element's lifetime.
    if (isHoldButton(this.el)) {
      attachHoldListeners(this)
    }
  },

  destroyed(this: HookContext) {
    this._cleanup?.()
  }
}
